import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { FormikProps } from 'formik';
import { InvoiceDetailsSettingsFormData } from 'models/forms';
import { FiCardType } from 'utils/payment-details-validation';

type DanishField = 'registrationNumber' | 'accountNumber' | 'fiCreditorNumber';

/** DD-3700: a Danish lab's reg.nr. + account number and its FI agreement (indbetalingskort). */
const DanishPaymentFields: FC<{ formik: FormikProps<InvoiceDetailsSettingsFormData> }> = ({ formik }) => {
    const { t: translate } = useTranslation(['lab-settings']);
    const fieldError = (field: DanishField) => {
        const message = formik.touched[field] ? formik.errors[field] : undefined;
        return { error: Boolean(message), message };
    };
    const registrationError = fieldError('registrationNumber');
    const accountError = fieldError('accountNumber');
    const creditorError = fieldError('fiCreditorNumber');
    const cardTypeError = formik.errors.fiCardType;

    return (
        <>
            <Grid item sm={12} md={6}>
                <TextField
                    id="registrationNumber"
                    label={translate('lab-settings:REGISTRATION_NUMBER')}
                    fullWidth
                    {...formik.getFieldProps('registrationNumber')}
                    error={registrationError.error}
                    helperText={registrationError.message || translate('lab-settings:REGISTRATION_NUMBER_INFO')}
                />
            </Grid>
            <Grid item sm={12} md={6}>
                <TextField
                    id="accountNumber"
                    label={translate('lab-settings:ACCOUNT_NUMBER')}
                    fullWidth
                    {...formik.getFieldProps('accountNumber')}
                    error={accountError.error}
                    helperText={accountError.message || translate('lab-settings:DANISH_ACCOUNT_NUMBER_INFO')}
                />
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    control={<Checkbox {...formik.getFieldProps({ name: 'fiEnabled', type: 'checkbox' })} />}
                    label={translate('lab-settings:FI_ENABLED')}
                />
                <Typography variant="body2" color="text.secondary">
                    {translate('lab-settings:FI_INFO')}
                </Typography>
            </Grid>
            {formik.values.fiEnabled ? (
                <>
                    <Grid item sm={12} md={6}>
                        <FormControl fullWidth error={Boolean(cardTypeError)}>
                            <InputLabel id="fi-card-type-label">{translate('lab-settings:FI_CARD_TYPE')}</InputLabel>
                            <Select
                                labelId="fi-card-type-label"
                                label={translate('lab-settings:FI_CARD_TYPE')}
                                {...formik.getFieldProps('fiCardType')}
                            >
                                <MenuItem value={FiCardType.FI_71}>
                                    {translate('lab-settings:FI_CARD_TYPE_71')}
                                </MenuItem>
                                <MenuItem value={FiCardType.FI_75}>
                                    {translate('lab-settings:FI_CARD_TYPE_75')}
                                </MenuItem>
                                <MenuItem value={FiCardType.FI_73}>
                                    {translate('lab-settings:FI_CARD_TYPE_73')}
                                </MenuItem>
                            </Select>
                            {cardTypeError ? <FormHelperText>{cardTypeError}</FormHelperText> : undefined}
                        </FormControl>
                    </Grid>
                    <Grid item sm={12} md={6}>
                        <TextField
                            id="fiCreditorNumber"
                            label={translate('lab-settings:FI_CREDITOR_NUMBER')}
                            fullWidth
                            {...formik.getFieldProps('fiCreditorNumber')}
                            error={creditorError.error}
                            helperText={creditorError.message || translate('lab-settings:FI_CREDITOR_NUMBER_INFO')}
                        />
                    </Grid>
                </>
            ) : undefined}
        </>
    );
};

export default DanishPaymentFields;
